import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { CustomEventCommand } from '../commands/hub/custom.event.command';
import { StyleCommand } from '../commands/hub/style.command';
import { VisitorPromotionImageCommand } from '../commands/hub/visitor.promotion.image.command';
import { ApiService } from '../common/services/api.service';
import { HubCustomBtn } from '../models/hub/hub.custom.btn';
import { HubVisitorPromotionImage } from '../models/hub/hub.visitor.promotion.image';
import { AllFrontendSettingsCommand } from '../commands/visitor/all.frontend.settings.command';

@Injectable()
export class CustomizationApiService {
  constructor(private apiService: ApiService) {}

  public getCustomSettings(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/custom-settings`);
  }

  public getAllFrontendSettings(hubName: string): Observable<AllFrontendSettingsCommand> {
    return this.apiService.get(`visitors/${hubName}/all-frontend-settings`);
  }

  public getAllFrontendSettingsForTP(hubName: string, regionId?): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/all-frontend-settings-for-tp?regionId=${regionId || ''}`);
  }

  public getStyleAndPDFSettings(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/style-and-pdf-settings`);
  }

  public getStyleAndPDFSettingsForHub(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/style-and-pdf-settings`);
  }

  public setStyle(hubName: string, style: StyleCommand): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/style`, style);
  }

  public getStyle(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/style`);
  }

  public setTPDetailsStyle(hubName: string, style: string): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/trip-planner-details-style`, { style });
  }

  public getTPDetailsStyle(hubName: string): Observable<string> {
    return this.apiService.get(`hubs/${hubName}/trip-planner-details-style`);
  }

  public getTripPlannerSettings(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/trip-planner-settings`);
  }

  public setTripPlannerSettings(hubName: string, settings: any): Observable<any> {
    return this.apiService.put(`hubs/${hubName}/trip-planner-settings`, settings);
  }

  public setMenuStyle(hubName: string, isSideBar: Boolean): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/menu-style`, { isSideBar: isSideBar });
  }

  public setCalendarStyle(hubName: string, calendarStyle: { event; place }): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/calendar-style`, { calendarStyle });
  }

  public getLogo(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/logo`);
  }

  public setLogo(hubName: string, logo: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/logo`, logo);
  }

  public setLogoFile(hubName: string, command: any): Observable<any> {
    let formData = new FormData();

    for (let property of Object.keys(command)) {
      if (command[property]) {
        formData.append(property, command[property]);
      }
    }

    if (!!command.image) {
      formData.set('image', command.image, command.image.name);
    } else {
      formData.set('image', null);
    }

    return this.apiService.put(`hubs/${hubName}/logo-file`, formData);
  }

  public getTPHomeAndTourSidebarImage(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/trip-planner-home-side-bar-image`);
  }

  public getTPHomeImage(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/trip-planner-home-image`);
  }

  public setTPHomeImage(hubName: string, logo: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/trip-planner-home-image`, logo);
  }

  public setTPHomeImageFile(hubName: string, command: any): Observable<void> {
    let formData = new FormData();

    for (let property of Object.keys(command)) {
      if (command[property]) {
        formData.append(property, command[property]);
      }
    }

    if (!!command.image) {
      formData.set('image', command.image, command.image.name);
    } else {
      formData.set('image', null);
    }

    return this.apiService.put(`hubs/${hubName}/trip-planner-home-image-file`, formData);
  }

  public setTourSidebarImageFile(hubName: string, data: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/tour-sidebar-image-file`, data);
  }

  public getPDFSettings(hubName: string): Observable<any> {
    return this.apiService.get(`hubs/${hubName}/pdf-settings`);
  }

  public setPDFSettings(hubName: string, pdfSettings: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/pdf-settings`, pdfSettings);
  }

  public setPDFEventHeaderImage(hubName: string, pdfSettings: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/event-header-pdf-image`, pdfSettings);
  }

  public setPDFPlaceHeaderImage(hubName: string, pdfSettings: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/place-header-pdf-image`, pdfSettings);
  }

  public setPDFTourHeaderImage(hubName: string, pdfSettings: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/tour-header-pdf-image`, pdfSettings);
  }

  public setPDFTripHeaderImage(hubName: string, pdfSettings: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/trip-header-pdf-image`, pdfSettings);
  }

  public setIsKeepTempClosedPlaceInApproved(hubName: string, isKeep: boolean): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/is-keep-temp-closed-place-in-approved`, { isKeep });
  }

  public getVisitorPDFSettings(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/pdf-settings`);
  }

  public setIsHideEditorChoice(hubName: string, isHideEditorChoice: Boolean): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/is-hide-editor-choice`, { isHideEditorChoice: isHideEditorChoice });
  }

  public saveEditorChoice(hubName: string, params: any): Observable<void> {
    return this.apiService.put(`hubs/${hubName}/editor-choice`, params);
  }

  public getMenuStyle(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/menu-style`);
  }

  public getIgVersion(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/ig-version`);
  }

  public getCalendarStyle(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/calendar-style`);
  }

  public getIsHideEditorChoice(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/is-Hide-editor-choice`);
  }

  public getEditorChoice(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/editor-choice`);
  }

  public getCustomEvent(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/custom-event`);
  }

  public setCustomEvent(hubName: string, command: any): Observable<HubCustomBtn> {
    return this.apiService.put(`hubs/${hubName}/custom-events`, {
      isTurnOn: command.isTurnOn,
      customEvents: command.customEvents,
    });
  }

  public setCustomPlace(hubName: string, command: any): Observable<HubCustomBtn> {
    return this.apiService.put(`hubs/${hubName}/custom-places`, {
      customPlaces: command.customPlaces,
      isTurnOn: command.isTurnOn,
    });
  }

  public getVisitorPromotionImage(hubName: string): Observable<HubVisitorPromotionImage> {
    return this.apiService.get(`visitors/${hubName}/promotion`);
  }

  public setVisitorPromotionImage(hubName, command: VisitorPromotionImageCommand) {
    // let formData = new FormData();

    // for (let property of Object.keys(command)) {
    //   if (command[property]) {
    //     formData.append(property, command[property]);
    //   }
    // }

    // if (!!command.image) {
    //   formData.set('image', command.image, command.image.name);
    // } else {
    //   formData.set('image', null);
    // }

    return this.apiService.put(`hubs/${hubName}/promotion`, command);
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/services/customization.api.service.ts